import { useQuery, type QueryKey } from '@tanstack/react-query';

export interface FilterOption<TId = number> {
  id: TId;
  label: string;
  disabled?: boolean;
}

export interface UseFilterOptionsConfig<TResponse, TId = number> {
  queryKey: QueryKey;
  queryFn: () => Promise<TResponse>;
  transform: (response: TResponse) => FilterOption<TId>[];
  enabled?: boolean;
  staleTime?: number;
  refetchOnMount?: boolean | 'always';
  gcTime?: number;
}

export interface ItemsWithIdAndName {
  id: number;
  name: string;
}

export function itemsToFilterOptions<T extends ItemsWithIdAndName>(
  response: unknown,
  invalidMessage = 'Filter options: invalid API response (expected JSON with items[])'
): FilterOption<number>[] {
  if (
    typeof response !== 'object' ||
    response == null ||
    !('items' in response) ||
    !Array.isArray((response as { items: unknown }).items)
  ) {
    throw new Error(invalidMessage);
  }
  return (response as { items: T[] }).items.map((item) => ({
    id: item.id,
    label: item.name,
  }));
}

export function useFilterOptions<TResponse, TId = number>(
  config: UseFilterOptionsConfig<TResponse, TId>
) {
  return useQuery({
    queryKey: config.queryKey,
    queryFn: config.queryFn,
    select: config.transform,
    enabled: config.enabled,
    staleTime: config.staleTime ?? 5 * 60 * 1000, // 5 minut default
    gcTime: config.gcTime,
    refetchOnMount: config.refetchOnMount,
  });
}
